import { useState, useEffect } from 'react';
import API, { formatError } from '../api/client';
import { Plus, Activity, Trash2, X, TrendingDown, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PatientSelect from '../components/PatientSelect';

const EMPTY_FORM = {
  patient_id: '', date: new Date().toISOString().split('T')[0],
  type_diabete: 'Type 2', glycemie_a_jeun: '', glycemie_post_prandiale: '',
  hba1c: '', tension_arterielle: '', poids: '',
  traitement: '', complications: '', objectifs: '', notes: ''
};

const NORMS = {
  glycemie_a_jeun: { min: 70, max: 100, label: 'mg/dL' },
  glycemie_post_prandiale: { min: 70, max: 140, label: 'mg/dL' },
  hba1c: { min: 0, max: 6.5, label: '%' },
};

function StatusBadge({ val, norm }) {
  if (!val || !norm) return null;
  const v = parseFloat(val);
  if (isNaN(v)) return null;
  const ok = v >= norm.min && v <= norm.max;
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ml-1 ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
      {ok ? 'Normal' : 'Élevé'}
    </span>
  );
}

export default function DiabetesPage() {
  const [records, setRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async (pid = selectedPatient) => {
    const params = pid ? { patient_id: pid } : {};
    const [r, p] = await Promise.all([
      API.get('/diabetes', { params }),
      API.get('/patients'),
    ]);
    setRecords(r.data);
    setPatients(p.data.data ?? p.data);
  };

  useEffect(() => { load(); }, []);

  const handlePatientChange = (pid) => {
    setSelectedPatient(pid);
    setForm(f => ({ ...f, patient_id: pid }));
    load(pid);
  };

  const handleSave = async () => {
    setError('');
    if (!form.patient_id) { setError('Veuillez sélectionner un patient.'); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === '') payload[k] = null;
        else if (['glycemie_a_jeun', 'glycemie_post_prandiale', 'hba1c', 'poids'].includes(k)) payload[k] = parseFloat(v) || null;
        else payload[k] = v;
      });
      await API.post('/diabetes', payload);
      setShowForm(false);
      setForm({ ...EMPTY_FORM, patient_id: selectedPatient });
      load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet enregistrement ?')) return;
    await API.delete(`/diabetes/${id}`);
    load();
  };

  const chartData = [...records].reverse().map(r => ({
    date: r.date,
    'Glycémie à jeun': r.glycemie_a_jeun,
    'HbA1c': r.hba1c,
  }));

  const F = ({ label, children }) => (
    <div className="flex flex-col space-y-1">
      <label className="text-xs font-medium text-stone-600">{label}</label>
      {children}
    </div>
  );

  const inp = (field, type = 'text', placeholder = '') => (
    <input type={type} placeholder={placeholder} value={form[field] || ''}
      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
  );

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="diabetes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Activity size={22} className="text-orange-500" />
            Module Diabète
          </h1>
          <p className="text-stone-500 text-sm mt-0.5">Suivi glycémique et HbA1c</p>
        </div>
        <button data-testid="new-diabetes-record-button" onClick={() => { setShowForm(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors">
          <Plus size={17} /> Nouveau Suivi
        </button>
      </div>

      {/* Patient filter */}
      <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-4">
        <label className="text-sm font-medium text-stone-600 whitespace-nowrap">Filtrer par patient:</label>
        <PatientSelect
          testId="diabetes-patient-filter"
          className="flex-1 max-w-md"
          placeholder="Tous les patients — rechercher (nom, CIN, id)…"
          value={selectedPatient}
          patients={patients}
          allowClear
          onChange={(id) => handlePatientChange(id)}
        />
      </div>

      {/* Chart */}
      {chartData.length > 1 && (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <h3 className="font-heading font-semibold text-stone-700 mb-4">Évolution glycémique</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#78716c' }} />
              <YAxis tick={{ fontSize: 11, fill: '#78716c' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: '8px', border: '1px solid #e7e5e4' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Glycémie à jeun" stroke="#f97316" strokeWidth={2} dot={{ r: 4 }} name="Glycémie à jeun (mg/dL)" />
              <Line type="monotone" dataKey="HbA1c" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} name="HbA1c (%)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full" data-testid="diabetes-table">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {['Date', 'Patient', 'Type', 'Glycémie à jeun', 'HbA1c', 'TA', 'Poids', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {records.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-stone-400 text-sm">
                <Activity size={36} className="text-stone-200 mx-auto mb-2" />
                Aucun enregistrement
              </td></tr>
            )}
            {records.map(r => {
              const patient = patients.find(p => p.id === r.patient_id);
              return (
                <tr key={r.id} className="hover:bg-stone-50/60 transition-colors" data-testid="diabetes-record-row">
                  <td className="px-4 py-3 text-sm text-stone-700">{r.date}</td>
                  <td className="px-4 py-3 text-sm font-medium text-stone-800">{patient ? `${patient.prenom} ${patient.nom}` : '—'}</td>
                  <td className="px-4 py-3">
                    {r.type_diabete && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">{r.type_diabete}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {r.glycemie_a_jeun ? (
                      <span className={parseFloat(r.glycemie_a_jeun) > 100 ? 'text-red-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                        {r.glycemie_a_jeun} mg/dL
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {r.hba1c ? (
                      <span className={parseFloat(r.hba1c) > 6.5 ? 'text-red-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                        {r.hba1c}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">{r.tension_arterielle || '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{r.poids ? `${r.poids} kg` : '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleDelete(r.id)}
                      className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="diabetes-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
              <h2 className="font-heading font-semibold text-stone-800 text-lg">Nouveau Suivi Diabète</h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-stone-400 hover:text-stone-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}

              <div className="grid grid-cols-2 gap-4">
                <F label="Patient *">
                  <PatientSelect
                    value={form.patient_id}
                    patients={patients}
                    onChange={(id) => setForm(f => ({ ...f, patient_id: id }))}
                  />
                </F>
                <F label="Date">{inp('date', 'date')}</F>
                <F label="Type de Diabète">
                  <select value={form.type_diabete} onChange={e => setForm(f => ({ ...f, type_diabete: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                    <option value="Type 1">Type 1</option>
                    <option value="Type 2">Type 2</option>
                    <option value="Gestationnel">Gestationnel</option>
                    <option value="LADA">LADA</option>
                    <option value="Autre">Autre</option>
                  </select>
                </F>
                <F label="Tension artérielle">{inp('tension_arterielle', 'text', 'Ex: 130/80 mmHg')}</F>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Glycémie & HbA1c</h3>
                <div className="grid grid-cols-3 gap-4">
                  <F label="Glycémie à jeun (mg/dL)">
                    <div>{inp('glycemie_a_jeun', 'number', '0.90')}</div>
                    <StatusBadge val={form.glycemie_a_jeun} norm={NORMS.glycemie_a_jeun} />
                  </F>
                  <F label="Glycémie post-prandiale (mg/dL)">
                    <div>{inp('glycemie_post_prandiale', 'number', '1.40')}</div>
                    <StatusBadge val={form.glycemie_post_prandiale} norm={NORMS.glycemie_post_prandiale} />
                  </F>
                  <F label="HbA1c (%)">
                    <div>{inp('hba1c', 'number', '6.5')}</div>
                    <StatusBadge val={form.hba1c} norm={NORMS.hba1c} />
                  </F>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <F label="Traitement en cours">
                  <textarea rows={2} placeholder="Médicaments, insuline, régime..." value={form.traitement}
                    onChange={e => setForm(f => ({ ...f, traitement: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
                </F>
                <F label="Complications">
                  <textarea rows={2} placeholder="Neuropathie, rétinopathie, néphropathie, plaies..." value={form.complications}
                    onChange={e => setForm(f => ({ ...f, complications: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
                </F>
                <F label="Objectifs thérapeutiques">
                  <textarea rows={2} placeholder="Objectifs glycémiques, tension, poids..." value={form.objectifs}
                    onChange={e => setForm(f => ({ ...f, objectifs: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
                </F>
                <F label="Notes">
                  <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
                </F>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-100">Annuler</button>
              <button data-testid="save-diabetes-button" onClick={handleSave} disabled={saving}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
